import { useCallback, useEffect, useState, type FC, type ReactNode } from 'react';
import slugify from 'dibs-slugify/exports/slugify';
import { sessionStorage } from 'dibs-browser-storage';
import { FormattedMessage } from 'dibs-react-intl';
import { trackFacetClick } from '../../../utils/tracking/searchBrowse/filterTracking';
import {
    isPriceFilter,
    isDimensionFilter,
    isFreeShippingFilter,
    constructStatsRange,
    isDeliveredByFilter,
} from '../sbSharedRefineMenuHelpers';
import { sbSharedRefineMenuMessages } from '../sbSharedRefineMenuMessages';
import { getPricePillValue } from '../../SbSharedAppliedFilters/sbSharedAppliedFiltersValueHelper';
import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2';
import { TABLE_SIZE_FILTER } from '../../../utils/abTestHelper';
import { hasKey } from 'dibs-ts-utils/exports/hasKey';

// components
import { ExpandingArea } from 'dibs-elements/exports/ExpandingArea';

// styles
import styles from './SbSharedRefineMenuFacet.scss';

export const SESSION_STORAGE_KEY = 'facetMenu';

function updateLocalStorage({
    isExpanded,
    storageFacetKey,
}: {
    isExpanded: boolean;
    storageFacetKey: string;
}): void {
    const sessionFacetMenu = sessionStorage.getItem(SESSION_STORAGE_KEY) || {};
    sessionStorage.setItem(
        SESSION_STORAGE_KEY,
        Object.assign(sessionFacetMenu, { [storageFacetKey]: isExpanded })
    );
}

type Props = {
    initiallyExpanded?: boolean;
    title: string;
    trackingTrigger?: string | null;
    appliedFiltersComponent?: ReactNode;
    children: ReactNode;
    storageFacetKey: string;
    selectedValues?: readonly ({
        readonly displayName?: string | null;
        readonly urlLabel?: string | null;
    } | null)[];
    measurementUnit?: string;
    titleMessage?: string;
    currency?: string;
    maxStatsRange?: string | null;
    isTablesL2?: boolean;
};

export const SbSharedRefineMenuFacet: FC<Props> = ({
    title,
    trackingTrigger,
    children,
    appliedFiltersComponent,
    storageFacetKey,
    initiallyExpanded = true,
    selectedValues = [],
    measurementUnit = '',
    titleMessage = '',
    currency = '',
    maxStatsRange = '',
    isTablesL2 = false,
}) => {
    const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
    useEffect(() => {
        const sessionFacetMenu = sessionStorage.getItem(SESSION_STORAGE_KEY) || {};
        const isOpen = hasKey(sessionFacetMenu, storageFacetKey)
            ? sessionFacetMenu[storageFacetKey]
            : undefined;
        if (isOpen !== undefined && isExpanded !== isOpen) {
            setIsExpanded(isOpen);
        }
    }, [storageFacetKey, isExpanded]);
    const onLabelClick = useCallback(() => {
        const newExpandedValue = !isExpanded;
        setIsExpanded(newExpandedValue);
        trackFacetClick(trackingTrigger, newExpandedValue);
        updateLocalStorage({ storageFacetKey, isExpanded: newExpandedValue });
        if (newExpandedValue) {
            if (isDimensionFilter(storageFacetKey) && isTablesL2) {
                trackAbTestV2Variant(TABLE_SIZE_FILTER);
            }
        }
    }, [isExpanded, trackingTrigger, storageFacetKey, isTablesL2]);

    const renderAppliedFilters = (): ReactNode[] => {
        if (!selectedValues.length) {
            return [];
        }

        return selectedValues.map(appliedValue => {
            const displayName = appliedValue?.displayName || null;
            const urlLabel = appliedValue?.urlLabel || null;
            let value: ReactNode = displayName;
            let filterTitle: ReactNode = titleMessage;
            let dimension = null;

            if (isDimensionFilter(storageFacetKey)) {
                const [min, max] = constructStatsRange(urlLabel, maxStatsRange);
                value = sbSharedRefineMenuMessages.minMaxFormattedMessage({
                    isMobile: true,
                    min,
                    max,
                });
                dimension = (
                    <span className={styles.dimension}>{measurementUnit.toLowerCase()}</span>
                );
            } else if (isPriceFilter(storageFacetKey)) {
                value = getPricePillValue({ currency, displayName, isMobile: true });
            } else if (isDeliveredByFilter(storageFacetKey)) {
                value = (
                    <FormattedMessage
                        id="sb.SbSharedRefineMenuFacet.valueWithinDays"
                        defaultMessage="Within {displayName} Days"
                        values={{ displayName }}
                    />
                );
            } else if (isFreeShippingFilter(storageFacetKey)) {
                filterTitle = sbSharedRefineMenuMessages.shippingOptions;
                value = sbSharedRefineMenuMessages.shippingFree;
            }

            return (
                <div key={`${storageFacetKey}-${value}`} className={styles.abTestSelectedValue}>
                    {value || filterTitle}
                    {dimension}
                </div>
            );
        });
    };

    return (
        <ExpandingArea
            showArrow
            dataTn={`facet-${slugify(title)}-expanding-area`}
            onClick={onLabelClick}
            label={
                <>
                    <div className={styles.title}>{title}</div>
                    {!isExpanded && renderAppliedFilters()}
                    {appliedFiltersComponent}
                </>
            }
            expanded={isExpanded}
            labelClass={styles.titleWrapper}
            wrapperClass={styles.container}
            expandingAreaClass={styles.facetContent}
        >
            {children}
        </ExpandingArea>
    );
};
